import React from 'react';
import {
    ConfirmModal,
    Text,
    TextAs,
    TextSizes,
} from '@clockout-dev/design-elements';

/**
 * Destructive confirmation for the Reconcile Account action. Owns the
 * warning copy; the page owns the open flag and the request.
 *
 * `onOk` always returns false so the modal never closes itself — the
 * controller closes it on success, and a failed request leaves it open
 * to retry or cancel.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {boolean} [props.loading] True while the request is in flight.
 * @param {() => void} props.onCancel
 * @param {() => Promise<void>} props.onConfirm
 */
function ReconcileAccountModal({ open, loading, onCancel, onConfirm }) {
    const handleOk = async () => {
        if (!loading) await onConfirm();
        return false;
    };

    return (
        <ConfirmModal
            title="Reconcile this account?"
            description="This is a permanent bookkeeping action on this user's ledger."
            open={open}
            onCancel={onCancel}
            onOk={handleOk}
            okText={loading ? 'Reconciling...' : 'Reconcile Account'}
            cancelText="Cancel"
            okButtonDestructive
            confirmLoading={!!loading}
            destroyOnHidden
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Text as={TextAs.P} size={TextSizes.SMALL}>
                    Confirming will mark ALL outstanding debt for this user — every unpaid
                    advance and every fee, across all pay periods — as settled. A single
                    aggregate settle entry will be written to the ledger, along with a
                    matching transfer record against their latest pay period.
                </Text>
                <Text as={TextAs.P} size={TextSizes.SMALL}>
                    No real funds are moved. This only updates Clockout&apos;s records: the
                    user will no longer appear overdue and will be able to request advances
                    again.
                </Text>
                <Text as={TextAs.P} size={TextSizes.SMALL}>
                    This action cannot be undone.
                </Text>
            </div>
        </ConfirmModal>
    );
}

export default ReconcileAccountModal;
